import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import founderImg from '../founder.jpeg';
import emailjs from '@emailjs/browser';

const products = [
  {
    id: 'healhub',
    number: '01',
    emoji: '🏥',
    name: 'HealHub',
    tagline: 'Clinic Management System',
    description: 'End-to-end clinic management for modern healthcare providers. Patient records, appointments, billing, prescriptions — unified in one intelligent platform.',
    status: 'LIVE',
    statusColor: '#22C55E',
    href: '/healhub',
    external: 'https://healhub.in',
    // No waitlist — has external site
    waitlistHref: null,
    tags: ['Healthcare', 'SaaS', 'Cloud'],
  },
  {
    id: 'suraksha',
    number: '02',
    emoji: '🛡️',
    name: 'Suraksha',
    tagline: 'AI Anti-Fraud Protection',
    description: "India's most advanced anti-fraud mobile app. Real-time scam call detection, AI voice clone protection, digital arrest alerts, and SOS emergency response.",
    status: 'COMING SOON',
    statusColor: '#F59E0B',
    href: '/suraksha',
    external: null,
    // Goes directly to waitlist section on Suraksha page
    waitlistHref: '/suraksha#waitlist',
    tags: ['Cybersecurity', 'AI', 'Mobile'],
  },
  {
    id: 'vedant',
    number: '03',
    emoji: '🤖',
    name: 'Vedant',
    tagline: 'AI Health Assistant',
    description: 'Conversational AI health assistant built for Indian patients. Understands symptoms in Hindi and 6 languages, recommends specialists, integrates with HealHub.',
    status: 'IN DEVELOPMENT',
    statusColor: '#A855F7',
    href: '/vedant',
    external: null,
    // Goes directly to pilot section on Vedant page
    waitlistHref: '/vedant#pilot',
    tags: ['AI', 'Healthcare', 'NLP'],
  },
];

const stats = [
  { value: '3', label: 'Products Built' },
  { value: '500+', label: 'Clinics Protected' },
  { value: '₹0', label: 'VC Funded' },
  { value: '10yr', label: 'Vision Horizon' },
];

const capabilities = [
  {
    icon: '⚡',
    title: 'AI-First Architecture',
    desc: 'We build products where artificial intelligence is the foundation, not a feature added as an afterthought.',
  },
  {
    icon: '🔒',
    title: 'Security by Design',
    desc: 'AES-256 encryption, DPDP Act 2023 compliance, zero-trust architecture — baked in from day one.',
  },
  {
    icon: '🏗️',
    title: 'Systems, Not Features',
    desc: 'Every product we ship is designed to compound in value over a 10-year horizon. Long-term thinking.',
  },
  {
    icon: '🇮🇳',
    title: 'Built for India',
    desc: "Designed around India's unique challenges — 4G optimised, multilingual, DPDP compliant, India-first.",
  },
  {
    icon: '❤️',
    title: 'Social Responsibility',
    desc: 'Protecting elderly citizens from fraud, digitising healthcare. Our products create measurable social good.',
  },
  {
    icon: '🚀',
    title: 'Bootstrapped Independence',
    desc: 'No VC pressure, no growth-at-all-costs. We build right because we answer only to our users.',
  },
];

const impactNumbers = [
  { value: '₹1,750 Cr', label: 'Lost to cyber fraud annually in India', icon: '🚨' },
  { value: '70%', label: 'Of clinics still use paper records', icon: '📋' },
  { value: '340%', label: 'Rise in digital arrest scams 2024', icon: '📞' },
];

const services = [
  {
    icon: '🚀',
    title: 'Custom Software Development',
    desc: 'End-to-end product development — from architecture design to production deployment. We build MVPs to enterprise systems.',
    items: ['MVP Development', 'Web Applications', 'Mobile Apps', 'API Design'],
  },
  {
    icon: '👥',
    title: 'Staff Augmentation',
    desc: 'Pre-vetted developers, designers, and AI engineers ready to embed in your team within 48 hours. No recruitment overhead.',
    items: ['Backend & Frontend', 'Mobile Development', 'AI & Data Engineers', 'DevOps & Cloud'],
  },
  {
    icon: '🎨',
    title: 'UX / UI Design',
    desc: 'User experience that converts. We design interfaces that are intuitive, beautiful, and backed by user research — not assumptions.',
    items: ['Product Design', 'Design Systems', 'UX Research', 'Figma Prototyping'],
  },
];

const industries = [
  { name: 'Healthcare & MedTech', icon: '🏥', href: '/healhub' },
  { name: 'Cybersecurity & FraudTech', icon: '🛡️', href: '/suraksha' },
  { name: 'Fintech & Banking', icon: '🏦', href: '/services' },
  { name: 'Education & EdTech', icon: '🎓', href: '/services' },
  { name: 'Enterprise SaaS', icon: '🏗️', href: '/services' },
  { name: 'E-Commerce & Retail', icon: '🛒', href: '/services' },
  { name: 'AI & Automation', icon: '🤖', href: '/services' },
  { name: 'Government & Public Sector', icon: '🏛️', href: '/services' },
];

// ── Founder Contact Form ──
function FounderForm() {
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [submitted, setSubmitted] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    setError('');

    try {
      await emailjs.send(
        'service_g9tvszg',       // ← Replace with your EmailJS Service ID
        'template_124o1la',      // ← Replace with your EmailJS Template ID
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone || 'Not provided',
          company: formData.company || 'Not provided',
          message: formData.message,
          to_name: 'Ashish Uniyal',
        },
        'u6gb-CCFGNTwlW3Ot'        // ← Replace with your EmailJS Public Key
      );
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please email us directly at a.uniyal@redcubicalsystems.com');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(59,130,246,0.2)',
    borderRadius: '10px',
    padding: '14px 16px',
    fontSize: '14px',
    color: '#fff',
    fontFamily: 'Inter, sans-serif',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: '700',
    color: 'rgba(255,255,255,0.4)',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    display: 'block',
    marginBottom: '6px',
  };

  if (submitted) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '48px 32px',
        background: 'rgba(59,130,246,0.06)',
        border: '1px solid rgba(59,130,246,0.2)',
        borderRadius: '16px',
      }}>
        <p style={{ fontSize: '48px', marginBottom: '16px' }}>✅</p>
        <h3 style={{
          fontFamily: 'Barlow Condensed, sans-serif',
          fontSize: '26px', fontWeight: '900',
          color: '#fff', textTransform: 'uppercase',
          marginBottom: '10px',
        }}>
          Message Sent!
        </h3>
        <p style={{
          fontSize: '14px',
          color: 'rgba(255,255,255,0.5)',
          lineHeight: '1.6',
        }}>
          Ashish will personally respond within 24 hours.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
    >
      {/* Row 1 — Name + Email */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}
        className="form-row">
        <div>
          <label style={labelStyle}>Your Name *</label>
          <input
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Full name"
            required
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          />
        </div>
        <div>
          <label style={labelStyle}>Email *</label>
          <input
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="you@company.com"
            required
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          />
        </div>
      </div>

      {/* Row 2 — Phone + Company */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}
        className="form-row">
        <div>
          <label style={labelStyle}>Phone Number</label>
          <input
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+91 98765 43210"
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          />
        </div>
        <div>
          <label style={labelStyle}>Company / Project</label>
          <input
            name="company"
            value={formData.company}
            onChange={handleChange}
            placeholder="Your company name"
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          />
        </div>
      </div>

      {/* Row 3 — Message */}
      <div>
        <label style={labelStyle}>What would you like to discuss? *</label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Tell me about your idea, problem, or project..."
          required
          rows={4}
          style={{ ...inputStyle, resize: 'vertical', minHeight: '110px' }}
          onFocus={e => (e.target.style.borderColor = '#3B82F6')}
          onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
        />
      </div>

      {/* Error message */}
      {error && (
        <p style={{
          fontSize: '13px',
          color: '#E63946',
          background: 'rgba(230,57,70,0.08)',
          border: '1px solid rgba(230,57,70,0.2)',
          padding: '10px 14px',
          borderRadius: '8px',
        }}>
          ⚠️ {error}
        </p>
      )}

      {/* Submit button */}
      <button
        type="submit"
        disabled={submitting}
        className="btn-primary"
        style={{
          width: '100%',
          justifyContent: 'center',
          padding: '16px',
          fontSize: '14px',
          opacity: submitting ? 0.7 : 1,
          cursor: submitting ? 'not-allowed' : 'pointer',
        }}
      >
        {submitting ? '⏳ Sending...' : 'Send Message to Ashish ↗'}
      </button>

      <p style={{
        fontSize: '11px',
        color: 'rgba(255,255,255,0.25)',
        textAlign: 'center',
      }}>
        We respond within 24 hours · Your data is never shared
      </p>
    </form>
  );
}

// ── Main Page ──
export default function Home() {
  return (
    <div className="page-wrapper">
      <SEOHead
        title="We Build Software That Protects and Empowers India"
        description="Redcubical Systems builds AI-powered software products for India — HealHub clinic management, Suraksha anti-fraud app, and Vedant AI health assistant."
        canonical="/"
        keywords="Redcubical Systems, HealHub, Suraksha, Vedant, software company India, AI software India, clinic management India, anti fraud app India"
      />

      {/* ── HERO ── */}
      <section
        className="hero-bg"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          padding: '0 32px 80px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{
          position: 'absolute', top: '-200px', right: '-200px',
          width: '700px', height: '700px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(37,99,235,0.25) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '-100px', left: '-100px',
          width: '400px', height: '400px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <div style={{ maxWidth: '1280px', margin: '0 auto', width: '100%', position: 'relative' }}>
          <div
  className="hero-badge"
  style={{
    marginTop: '24px',
    marginBottom: '40px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    background: 'rgba(255,255,255,0.05)',
    padding: '8px 16px',
    borderRadius: '50px',
    border: '1px solid rgba(255,255,255,0.1)'
  }}
>
  <span>🇮🇳</span>
  <span style={{ fontSize: '13px', fontWeight: '500', color: '#fff', letterSpacing: '0.5px' }}>
    Built in India — Built for the World
  </span>
</div>

          <h1 className="display-heading" style={{ marginBottom: '32px', maxWidth: '1100px' }}>
            WE BUILD<br />
            <span className="gradient-text">SOFTWARE</span><br />
            THAT MATTERS
          </h1>

          <div style={{ display: 'grid', gap: '48px', alignItems: 'flex-end', marginTop: '16px' }}
            className="hero-bottom-grid">
            <p style={{
              fontSize: 'clamp(16px, 2vw, 20px)',
              color: 'rgba(255,255,255,0.6)',
              lineHeight: '1.7', maxWidth: '520px',
            }}>
              From protecting elderly citizens against cyber fraud to digitising India's
              healthcare infrastructure — we build intelligent, secure software that
              creates lasting change.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '16px' }}
              className="hero-cta-group">
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Link to="/products" className="btn-primary">Explore Products ↗</Link>
                <Link to="/contact" className="btn-outline">Start a Conversation</Link>
              </div>
              <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px' }}>
                HEALTHCARE · CYBERSECURITY · AI
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section style={{
        background: 'rgba(255,255,255,0.02)',
        borderTop: '1px solid rgba(59,130,246,0.12)',
        borderBottom: '1px solid rgba(59,130,246,0.12)',
        padding: '0 32px',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'grid' }}
          className="stats-grid">
          {stats.map((stat, i) => (
            <div key={i} style={{
              padding: '40px 24px', textAlign: 'center',
              borderRight: i < 3 ? '1px solid rgba(59,130,246,0.12)' : 'none',
            }}>
              <p className="number-display" style={{ marginBottom: '8px' }}>{stat.value}</p>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.5px' }}>
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── PRODUCTS ── */}
      <section style={{ padding: '120px 32px', background: 'var(--dark)' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ gap: '48px', alignItems: 'flex-end', marginBottom: '80px' }}
            className="section-header-grid">
            <div>
              <span className="section-label">Our Products</span>
              <h2 className="section-title">
                THREE PRODUCTS.<br />
                <span className="gradient-text">ONE VISION.</span>
              </h2>
            </div>
            <div>
              <p className="section-subtitle">
                Each product targets a critical gap in India's digital infrastructure.
                Healthcare management, cyber safety, and AI-powered health guidance.
              </p>
              <Link to="/products" className="btn-outline" style={{ marginTop: '24px' }}>
                View All Products →
              </Link>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {products.map((product) => (
              <div
                key={product.id}
                className="product-row"
                style={{
                  display: 'grid',
                  gap: '32px',
                  alignItems: 'center',
                  padding: '40px 0',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  transition: 'all 0.3s',
                  cursor: 'pointer',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.paddingLeft = '16px';
                  e.currentTarget.style.borderColor = 'rgba(59,130,246,0.3)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.paddingLeft = '0';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                }}
              >
                <span style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '48px', fontWeight: '900',
                  color: 'rgba(255,255,255,0.08)', lineHeight: 1,
                }}>
                  {product.number}
                </span>

                <div>
                  <div style={{
                    display: 'flex', alignItems: 'center',
                    gap: '16px', marginBottom: '12px', flexWrap: 'wrap',
                  }}>
                    <span style={{ fontSize: '28px' }}>{product.emoji}</span>
                    <h3 style={{
                      fontFamily: 'Barlow Condensed, sans-serif',
                      fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: '900',
                      color: '#fff', textTransform: 'uppercase',
                      letterSpacing: '-0.5px', lineHeight: 1,
                    }}>{product.name}</h3>
                    <span style={{
                      fontSize: '11px', fontWeight: '700',
                      color: product.statusColor,
                      background: `${product.statusColor}18`,
                      border: `1px solid ${product.statusColor}35`,
                      padding: '4px 12px', borderRadius: '50px',
                      letterSpacing: '0.5px',
                    }}>{product.status}</span>
                  </div>
                  <p style={{
                    fontSize: '14px', color: 'rgba(255,255,255,0.5)',
                    marginBottom: '12px', letterSpacing: '1px',
                    textTransform: 'uppercase', fontWeight: '600',
                  }}>{product.tagline}</p>
                  <p style={{
                    fontSize: '15px', color: 'rgba(255,255,255,0.55)',
                    lineHeight: '1.6', maxWidth: '580px', marginBottom: '16px',
                  }}>{product.description}</p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {product.tags.map(tag => (
                      <span key={tag} className="tag">{tag}</span>
                    ))}
                  </div>
                </div>

                                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    alignItems: 'stretch',
                    width: '180px',
                    flexShrink: 0,
                  }}
                  className="product-cta"
                >
                  <Link
                    to={product.href}
                    className="btn-blue"
                    style={{
                      padding: '12px 20px',
                      fontSize: '13px',
                      justifyContent: 'center',
                      textAlign: 'center',
                    }}
                  >
                    Learn More →
                  </Link>
                  {product.external ? (
                    <a
                      href={product.external}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-outline"
                      style={{
                        padding: '12px 20px',
                        fontSize: '13px',
                        justifyContent: 'center',
                        textAlign: 'center',
                      }}
                    >
                      Visit Site ↗
                    </a>
                  ) : product.waitlistHref ? (
                    <Link
                      to={product.waitlistHref}
                      className="btn-outline"
                      style={{
                        padding: '12px 20px',
                        fontSize: '13px',
                        justifyContent: 'center',
                        textAlign: 'center',
                      }}
                    >
                      Join Waitlist →
                    </Link>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY REDCUBICAL ── */}
      <section className="section-blue-glow" style={{ padding: '120px 32px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '72px' }}>
            <span className="section-label">Why Redcubical</span>
            <h2 className="section-title" style={{ marginBottom: '20px' }}>
              WE BUILD SYSTEMS.<br />
              <span className="gradient-text">NOT JUST SOFTWARE.</span>
            </h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              The difference between a feature and a system is the difference between solving
              a problem once and building a platform that compounds in value every single day.
            </p>
          </div>

          <div style={{
            gap: '2px',
            background: 'rgba(59,130,246,0.08)',
            border: '1px solid rgba(59,130,246,0.12)',
            borderRadius: '20px', overflow: 'hidden',
          }} className="capabilities-grid">
            {capabilities.map((item, i) => (
              <div key={i} style={{
                padding: '40px 36px',
                background: 'rgba(5,8,16,0.8)',
                transition: 'all 0.3s',
                borderRight: i % 3 !== 2 ? '1px solid rgba(59,130,246,0.08)' : 'none',
                borderBottom: i < 3 ? '1px solid rgba(59,130,246,0.08)' : 'none',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(5,8,16,0.8)'}
              >
                <div style={{
                  width: '52px', height: '52px',
                  background: 'rgba(59,130,246,0.1)',
                  border: '1px solid rgba(59,130,246,0.2)',
                  borderRadius: '12px',
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px', marginBottom: '20px',
                }}>{item.icon}</div>
                <h3 style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '20px', fontWeight: '900',
                  color: '#fff', textTransform: 'uppercase',
                  marginBottom: '12px', letterSpacing: '0.3px',
                }}>{item.title}</h3>
                <p style={{
                  fontSize: '14px', color: 'rgba(255,255,255,0.5)', lineHeight: '1.65',
                }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── IMPACT NUMBERS ── */}
      <section style={{
        padding: '120px 32px', background: 'var(--dark)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ gap: '80px', alignItems: 'center' }} className="impact-grid">
            <div>
              <span className="section-label">The Problem We're Solving</span>
              <h2 className="section-title" style={{ marginBottom: '24px' }}>
                THE SCALE OF<br />
                <span className="gradient-text">THE PROBLEM</span>
              </h2>
              <p className="body-text" style={{ marginBottom: '32px' }}>
                India loses ₹1,750 crore to cyber fraud every year. 70% of clinics still run
                on paper. We build software that directly addresses these realities — not for
                press releases, but because it is the right thing to do.
              </p>
              <Link to="/about" className="btn-blue">Read Our Mission →</Link>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {impactNumbers.map((item, i) => (
                <div key={i} style={{
                  display: 'flex', gap: '24px', alignItems: 'center',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(59,130,246,0.12)',
                  borderRadius: '16px', padding: '28px', transition: 'all 0.3s',
                }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'rgba(59,130,246,0.06)';
                    e.currentTarget.style.borderColor = 'rgba(59,130,246,0.25)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                    e.currentTarget.style.borderColor = 'rgba(59,130,246,0.12)';
                  }}
                >
                  <span style={{ fontSize: '32px', flexShrink: 0 }}>{item.icon}</span>
                  <div>
                    <p style={{
                      fontFamily: 'Barlow Condensed, sans-serif',
                      fontSize: '36px', fontWeight: '900',
                      color: '#fff', lineHeight: 1, marginBottom: '4px',
                    }}>{item.value}</p>
                    <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)' }}>
                      {item.label}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICES OVERVIEW ── */}
      <section style={{
        padding: '120px 32px', background: 'var(--dark-2)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ gap: '80px', alignItems: 'flex-start', marginBottom: '64px' }}
            className="services-header-grid">
            <div>
              <span className="section-label">What We Do</span>
              <h2 className="section-title">
                WE BUILD<br />
                <span className="gradient-text">SYSTEMS.</span><br />
                NOT FEATURES.
              </h2>
            </div>
            <div>
              <p className="body-text" style={{ marginBottom: '28px' }}>
                From zero-to-one product development to augmenting your existing engineering
                team — we bring the talent, process, and systems thinking to build software
                that lasts.
              </p>
              <Link to="/services" className="btn-blue">Explore All Services →</Link>
            </div>
          </div>

          <div style={{
            gap: '2px',
            background: 'rgba(59,130,246,0.06)',
            border: '1px solid rgba(59,130,246,0.12)',
            borderRadius: '20px', overflow: 'hidden',
          }} className="services-cards-grid">
            {services.map((service, i) => (
              <div key={i} style={{
                padding: '40px 36px',
                background: 'rgba(5,8,16,0.9)',
                borderRight: i < 2 ? '1px solid rgba(59,130,246,0.08)' : 'none',
                transition: 'background 0.3s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(5,8,16,0.9)'}
              >
                <div style={{
                  width: '52px', height: '52px',
                  background: 'rgba(59,130,246,0.1)',
                  border: '1px solid rgba(59,130,246,0.2)',
                  borderRadius: '12px',
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px', marginBottom: '20px',
                }}>{service.icon}</div>
                <h3 style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '22px', fontWeight: '900',
                  color: '#fff', textTransform: 'uppercase', marginBottom: '12px',
                }}>{service.title}</h3>
                <p style={{
                  fontSize: '14px', color: 'rgba(255,255,255,0.5)',
                  lineHeight: '1.65', marginBottom: '20px',
                }}>{service.desc}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {service.items.map(item => (
                    <div key={item} style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      fontSize: '13px', color: 'rgba(255,255,255,0.45)',
                    }}>
                      <span style={{
                        width: '4px', height: '4px',
                        background: '#3B82F6', borderRadius: '50%', flexShrink: 0,
                      }} />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INDUSTRY EXPERTISE ── */}
      <section style={{
        padding: '120px 32px', background: 'var(--dark)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ gap: '80px', alignItems: 'flex-start' }} className="industry-grid">
            <div style={{ position: 'sticky', top: '100px' }}>
              <span className="section-label">Industry Expertise</span>
              <h2 className="section-title" style={{ marginBottom: '24px' }}>
                INDUSTRY<br />
                <span className="gradient-text">EXPERTISE</span>
              </h2>
              <p className="body-text">
                We bring deep domain knowledge across India's most critical and fast-growing
                sectors. Every product and project we take benefits from years of hands-on
                experience in these industries.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {industries.map((industry, i) => (
                <Link
                  key={industry.name}
                  to={industry.href}
                  style={{
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '24px 28px',
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(59,130,246,0.1)',
                    borderRadius: i === 0 ? '14px 14px 0 0'
                      : i === industries.length - 1 ? '0 0 14px 14px' : '0',
                    marginTop: i === 0 ? '0' : '-1px',
                    textDecoration: 'none',
                    transition: 'all 0.25s',
                    color: '#fff', gap: '16px',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'rgba(59,130,246,0.1)';
                    e.currentTarget.style.borderColor = 'rgba(59,130,246,0.3)';
                    e.currentTarget.style.paddingLeft = '36px';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                    e.currentTarget.style.borderColor = 'rgba(59,130,246,0.1)';
                    e.currentTarget.style.paddingLeft = '28px';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <span style={{ fontSize: '22px' }}>{industry.icon}</span>
                    <span style={{
                      fontFamily: 'Barlow Condensed, sans-serif',
                      fontSize: '20px', fontWeight: '900',
                      textTransform: 'uppercase', letterSpacing: '0.3px',
                    }}>{industry.name}</span>
                  </div>
                  <span style={{ fontSize: '18px', color: '#3B82F6', flexShrink: 0 }}>↗</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── TALK TO OUR FOUNDER ── */}
      <section style={{
        padding: '0', background: '#000',
        borderTop: '1px solid rgba(59,130,246,0.1)',
        overflow: 'hidden',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ minHeight: '600px' }} className="founder-grid">

            {/* Left — Photo */}
            <div style={{
              position: 'relative', overflow: 'hidden',
              background: 'linear-gradient(135deg, #0A0E1A 0%, #0D1B3E 100%)',
              display: 'flex', alignItems: 'flex-end',
              justifyContent: 'center', minHeight: '500px',
            }}>
              <div style={{
                position: 'absolute', bottom: 0, left: '50%',
                transform: 'translateX(-50%)',
                width: '400px', height: '400px',
                background: 'radial-gradient(circle at bottom, rgba(37,99,235,0.3) 0%, transparent 70%)',
                pointerEvents: 'none',
              }} />
              <img
                src={founderImg}
                alt="Founder — Redcubical Systems"
                style={{
                  width: '100%', maxWidth: '420px',
                  height: '100%', objectFit: 'cover',
                  objectPosition: 'top center',
                  position: 'relative', zIndex: 1,
                  filter: 'contrast(1.05) brightness(0.95)',
                }}
              />
              <div style={{
                position: 'absolute', bottom: '32px', left: '32px', zIndex: 2,
              }}>
                <p style={{
  fontFamily: 'Barlow Condensed, sans-serif',
  fontSize: '22px',
  fontWeight: '900',
  color: '#fff',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  marginBottom: '4px',
}}>
  Ashish Uniyal
</p>
<p style={{
  fontFamily: 'Barlow Condensed, sans-serif',
  fontSize: '15px',
  fontWeight: '700',
  color: 'rgba(255,255,255,0.6)',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  marginBottom: '4px',
}}>
  Founder
</p>
<p style={{
  fontSize: '12px',
  color: 'rgba(255,255,255,0.35)',
  letterSpacing: '0.5px',
}}>
  Redcubical Systems Private Limited
</p>
              </div>
            </div>

            {/* Right — Form */}
            <div style={{
              padding: '72px 64px',
              background: 'rgba(5,8,16,1)',
              display: 'flex', flexDirection: 'column', justifyContent: 'center',
            }}>
              <span className="section-label" style={{ marginBottom: '16px' }}>
                Direct Access
              </span>
              <h2 className="section-title" style={{ marginBottom: '12px' }}>
                TALK TO<br />
                <span className="gradient-text">OUR FOUNDER</span>
              </h2>
              <p style={{
                fontSize: '15px', color: 'rgba(255,255,255,0.45)',
                marginBottom: '36px', lineHeight: '1.6',
              }}>
                Have an idea worth building? A problem worth solving? Skip the sales team —
                talk directly to our founder. We respond within 24 hours.
              </p>
              <FounderForm />
            </div>
          </div>
        </div>
      </section>

      {/* ── BUILD WITH US ── */}
      <section style={{
        padding: '120px 32px', background: 'var(--dark-2)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(37,99,235,0.15) 0%, rgba(59,130,246,0.05) 100%)',
            border: '1px solid rgba(59,130,246,0.2)',
            borderRadius: '24px', padding: '80px 64px',
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', top: '-100px', right: '-100px',
              width: '400px', height: '400px', borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(37,99,235,0.2) 0%, transparent 70%)',
              pointerEvents: 'none',
            }} />
            <div style={{ gap: '48px', alignItems: 'center' }} className="cta-grid">
              <div>
                <span className="section-label">Build With Us</span>
                <h2 className="section-title" style={{ marginBottom: '20px' }}>
                  HAVE A PROBLEM<br />
                  <span className="gradient-text">WORTH SOLVING?</span>
                </h2>
                <p className="body-text" style={{ maxWidth: '520px' }}>
                  We take on select client projects — MVP development, AI system design,
                  and product automation. We take 3–4 projects per quarter. If your problem
                  is interesting, we want to hear about it.
                </p>
              </div>
              <div style={{
                display: 'flex', flexDirection: 'column',
                gap: '12px', alignItems: 'flex-start', flexShrink: 0,
              }}>
                <Link to="/services" className="btn-primary" style={{ whiteSpace: 'nowrap' }}>
                  Explore Services ↗
                </Link>
                <Link to="/contact" className="btn-outline" style={{ whiteSpace: 'nowrap' }}>
                  Start a Conversation
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── ALL RESPONSIVE STYLES ── */}
      <style>{`
        .hero-bottom-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
        }
        .stats-grid {
          grid-template-columns: repeat(4, 1fr);
        }
        .section-header-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
        }
        .capabilities-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
        }
        .impact-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
        }
        .services-header-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
        }
        .services-cards-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
        }
        .industry-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
        }
        .founder-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
        }
        .cta-grid {
          display: grid;
          grid-template-columns: 1fr auto;
        }
        .product-row { grid-template-columns: 80px 1fr 200px; }
        }
        .form-row {
          grid-template-columns: 1fr 1fr;
        }
        @media (max-width: 1024px) {
          .capabilities-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .services-cards-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .hero-bottom-grid {
            grid-template-columns: 1fr !important;
          }
          .hero-cta-group {
            align-items: flex-start !important;
          }
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .section-header-grid {
            grid-template-columns: 1fr !important;
          }
          .capabilities-grid {
            grid-template-columns: 1fr !important;
          }
          .impact-grid {
            grid-template-columns: 1fr !important;
          }
          .services-header-grid {
            grid-template-columns: 1fr !important;
          }
          .services-cards-grid {
            grid-template-columns: 1fr !important;
          }
          .industry-grid {
            grid-template-columns: 1fr !important;
          }
          .founder-grid {
            grid-template-columns: 1fr !important;
          }
          .cta-grid {
            grid-template-columns: 1fr !important;
          }
          .product-row {
            grid-template-columns: 60px 1fr !important;
          }
          .product-cta {
            grid-column: 1 / -1;
            align-items: flex-start !important;
            flex-direction: row !important;
            flex-wrap: wrap;
          }
          .form-row {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>
    </div>
  );
}